/**
 * Game setup: deals starting positions and tickets, builds the initial public
 * `GameState`.
 *
 * IMPORTANT: Mr. X's true station is *private* and is NOT part of the public
 * `GameState`. `setupGame` returns both the public state and Mr. X's secret
 * starting station so the caller (e.g. a Firestore service layer) can write
 * the secret to a separate, restricted document.
 */

import type {
  DetectiveState,
  DetectiveTickets,
  GameState,
  MrxPublicState,
} from '../types'
import type { PlayerColor } from '../../platform/types'
import {
  DETECTIVE_TICKETS,
  MAX_DETECTIVES,
  MIN_DETECTIVES,
  MRX_TICKETS,
  START_POSITIONS,
} from './constants'

export interface SetupPlayer {
  uid: string
  displayName: string
  color: PlayerColor
  avatar: string
}

export interface SetupInput {
  /** The non-Mr.X players (detectives), in the seating order they'll act in. */
  players: SetupPlayer[]
  /** The Mr. X player. Includes uid, displayName, avatar (no color — Mr. X is black). */
  mrx: { uid: string; displayName: string; avatar: string }
  /**
   * Optional deterministic RNG returning [0, 1). If omitted, `Math.random` is
   * used. Tests should always pass an `rng`.
   */
  rng?: () => number
  /**
   * Per-detective starting ticket counts. Defaults to the standard
   * `DETECTIVE_TICKETS` (11/8/4). Used to support easy-mode variants
   * (e.g. doubled or effectively-unlimited tickets).
   */
  detectiveTickets?: DetectiveTickets
  /**
   * Mr. X's starting black-ticket count. Defaults to `MRX_TICKETS.black` (5,
   * the Milton Bradley value). The Ravensburger edition uses one per detective.
   */
  mrxBlackTickets?: number
  /**
   * Mr. X's starting double-move count. Defaults to `MRX_TICKETS.doubleMove`
   * (2, Milton Bradley). Ravensburger uses 3.
   */
  mrxDoubleMoves?: number
}

export interface SetupResult {
  /** Public state suitable for storing in the shared game doc. */
  state: GameState
  /**
   * Mr. X's true starting station. The caller is responsible for storing
   * this somewhere only Mr. X (and the engine, when invoked) can read.
   */
  mrxStation: number
}

/**
 * Validate inputs, shuffle the start-position pool, deal positions and
 * tickets, and return both the public state and Mr. X's secret station.
 */
export function setupGame(input: SetupInput): SetupResult {
  const {
    players,
    mrx,
    rng = Math.random,
    detectiveTickets = DETECTIVE_TICKETS,
    mrxBlackTickets = MRX_TICKETS.black,
    mrxDoubleMoves = MRX_TICKETS.doubleMove,
  } = input

  const detectiveCount = players.length
  if (detectiveCount < MIN_DETECTIVES || detectiveCount > MAX_DETECTIVES) {
    throw new Error(
      `Invalid detective count: ${detectiveCount} (must be between ${MIN_DETECTIVES} and ${MAX_DETECTIVES}).`,
    )
  }
  // Reject duplicate uids.
  const uids = new Set<string>([mrx.uid, ...players.map((p) => p.uid)])
  if (uids.size !== detectiveCount + 1) {
    throw new Error('Duplicate uids in setup input.')
  }

  // Shuffle a copy of START_POSITIONS, deal Mr. X first then each detective.
  const pool = shuffle([...START_POSITIONS], rng)
  const mrxStation = pool.shift()!
  const detectiveStations = pool.slice(0, detectiveCount)

  const detectives: DetectiveState[] = players.map((p, i) => ({
    uid: p.uid,
    displayName: p.displayName,
    color: p.color,
    avatar: p.avatar,
    station: detectiveStations[i],
    tickets: { ...detectiveTickets },
    stranded: false,
    // Index 0 = starting station; entries appended on each successful move.
    stationHistory: [detectiveStations[i]],
  }))

  const mrxPublic: MrxPublicState = {
    uid: mrx.uid,
    displayName: mrx.displayName,
    avatar: mrx.avatar,
    transportLog: [],
    blackTickets: mrxBlackTickets,
    doubleMoves: mrxDoubleMoves,
    lastRevealedRound: null,
    lastRevealedStation: null,
  }

  const state: GameState = {
    round: 1,
    phase: 'mrx',
    awaitingUids: [mrx.uid],
    detectives,
    mrx: mrxPublic,
    status: 'active',
    winner: null,
    // Populated by the service layer at game-end. While the game is active
    // every client has access to detective histories via `detectives[i].
    // stationHistory`; Mr. X's history is private (held in `private/mrx`)
    // until the service layer denormalizes it on game-end.
    replay: null,
  }

  return { state, mrxStation }
}

/** Fisher-Yates shuffle, mutates input array, returns the same reference. */
function shuffle<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}
